import {
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  FormProps,
  notification,
  Row,
  Select,
  Table,
  TableColumnsType,
  TableProps,
  Button,
} from 'antd';
import Title from 'antd/es/typography/Title';
import locale from 'antd/es/date-picker/locale/th_TH';
import dayjs from 'dayjs';
import { useState } from 'react';
import { docTypeReceipt, formatDate } from '@/utilities/dataUtils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import CustomerSelector from '@/components/selector/customer-selector';
import { ICustomer } from '@/interfaces/customer';
import { IReceiptNo, IServerInfoRes } from '@/interfaces/misc';
import ReceiptNoSelector from '@/components/selector/receipt-no-selector';
import { getServerInfo } from '@/adapters/misc';
import SellerInvoiceModal from './components/seller-invoice-modal';
import classNames from 'classnames';
import { jobProcess } from '@/adapters/job';
import { getQueryParam } from '@/utilities/queryUtils';
import {
  receiptEntry,
  receiptVerify,
  searchReceipt,
  getReceiptLines,
} from '@/adapters/receipt';
import {
  getReceiptStatus,
  getReceiptStatusColor,
  getReceiptStatusOptions,
} from '@/helpers/receipt';
import {
  IReceipt,
  IReceiptLine,
  IReceiptSearchReq,
} from '@/interfaces/receipt';

const { RangePicker } = DatePicker;

const _criteria: IReceiptSearchReq = {
  receiptNoFrom: null,
  receiptNoTo: null,
  dateFrom: dayjs().startOf('month').format('YYYY-MM-DD'),
  dateTo: dayjs().endOf('month').format('YYYY-MM-DD'),
  docStatus: 'PENDING',
  customerFrom: null,
  customerTo: null,
};

const BillingPage: React.FC = () => {
  const job = getQueryParam('job');
  const [form] = Form.useForm();
  const [criteria, setCriteria] = useState<IReceiptSearchReq>({
    ..._criteria,
  });
  const [selectedRowKey, setSelectedRowKey] = useState<IReceipt[]>([]);
  const [dateRange, setDateRange] = useState({
    dateFrom: _criteria.dateFrom,
    dateTo: _criteria.dateTo,
  });
  const receiptNoFromValue = Form.useWatch('receiptNoFrom', form);
  const customerFromValue = Form.useWatch('customerFrom', form);

  const [invoiceModal, setInvoiceModal] = useState<{
    open: boolean;
    invoiceList: IReceiptLine[];
  }>({
    open: false,
    invoiceList: [],
  });

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 14 },
    },
  };

  const onFinish: FormProps<IReceiptSearchReq>['onFinish'] = (values) => {
    console.log('values', values);
    const dateFrom = values.docDate?.[0].format('YYYY-MM-DD') || '';
    const dateTo = values.docDate?.[1].format('YYYY-MM-DD') || '';

    setCriteria({
      ...values,
      dateFrom,
      dateTo,
      docDate: values.docDate,
    });

    // Update dateRange to match search criteria
    setDateRange({
      dateFrom,
      dateTo,
    });
  };

  const onClear = () => {
    form.setFieldsValue({
      ..._criteria,
      docDate: [dayjs(_criteria.dateFrom), dayjs(_criteria.dateTo)],
    });
    setCriteria({
      ..._criteria,
    });
    setDateRange({
      dateFrom: _criteria.dateFrom,
      dateTo: _criteria.dateTo,
    });
  };

  const columns: TableColumnsType<IReceipt> = [
    {
      title: '#',
      dataIndex: 'No',
      width: 40,
      align: 'center',
      className: 'text-bold',
      render: (_, rec, i) => <div className="font-weight-bold">{i + 1}</div>,
    },
    {
      title: 'เลขที่เอกสาร',
      dataIndex: 'ID',
      align: 'left',
      className: 'text-bold',
      width: 80,
      fixed: 'left',
    },
    {
      title: 'ประเภทเอกสาร',
      dataIndex: 'TYPECODE',
      align: 'center',
      className: 'text-bold',
      width: 80,
      render: (_) => docTypeReceipt(_),
    },
    {
      title: 'วันที่เอกสาร',
      dataIndex: 'ISSUEDATETIME',
      align: 'center',
      width: 80,
      render: (_) => formatDate(_),
    },
    {
      title: 'ลูกค้า',
      dataIndex: 'BUYER_NAME_TEXT',
      align: 'left',
      className: 'text-bold',
      width: 300,
    },
    {
      title: 'สถานะการส่ง',
      dataIndex: 'AR_MSTR_BMESSSTATUS',
      align: 'center',
      width: 10,
      render: (_) => (
        <div style={{ backgroundColor: getReceiptStatusColor(_) }}>
          {getReceiptStatus(_)}
        </div>
      ),
    },
    {
      title: '',
      dataIndex: '',
      align: 'center',
      width: 10,
      render: (_, record) => (
        <Button
          ghost
          type="primary"
          size="small"
          onClick={() => handleGetReceiptLines(record)}
        >
          รายละเอียด
        </Button>
      ),
    },
  ];

  const { data, isLoading, refetch } = useQuery<AxiosResponse<any>, Error>({
    queryKey: ['search-receipt', criteria],
    queryFn: async () => await searchReceipt(criteria),
  });

  const { data: serverInfo } = useQuery<AxiosResponse<IServerInfoRes>, Error>({
    queryKey: ['search-server-info'],
    queryFn: async () => await getServerInfo(),
  });
  const receipts: IReceipt[] = data?.data?.data || [];

  const rowSelection: TableProps<IReceipt>['rowSelection'] = {
    selectedRowKeys: selectedRowKey.map((row) => row.ID),
    onChange: (selectedRowKeys: React.Key[], selectedRows: IReceipt[]) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        'selectedRows: ',
        selectedRows
      );
      setSelectedRowKey(selectedRows);
    },
    getCheckboxProps: (record: IReceipt) => ({
      disabled: !!record.AR_MSTR_BMESSID && record.AR_MSTR_BMESSSTATUS !== 'PC',
    }),
  };

  const mutations = useMutation({
    mutationFn: async (data: IReceipt[] | IReceipt) => await receiptEntry(data),
    mutationKey: ['receipt-entry'],
    onSuccess: (data) => {
      const receipts: IReceipt[] = data.data?.data || [];
      receipts.forEach((res: any) => {
        if (res.status === 'fulfilled') {
          const refNo = res.value?.refNo || '';
          notification.success({
            message: `${refNo} ส่งเอกสารสำเร็จ`,
          });
        } else {
          const refNo = res.reason?.refNo || '';
          const errorMessage = res.reason?.message || 'ส่งเอกสารไม่สำเร็จ';
          notification.error({
            message: `${refNo} ${errorMessage}`,
          });
        }
      });
      const hasSuccess = receipts.some(
        (res: any) => res.status === 'fulfilled'
      );
      if (hasSuccess) {
        refetch();
        setSelectedRowKey([]);
      }
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  function onEntry(): void {
    mutations.mutateAsync(selectedRowKey);
  }

  const mutationsVerify = useMutation({
    mutationFn: async (data: IReceipt[] | IReceipt) =>
      await receiptVerify(data),
    mutationKey: ['receipt-verify'],
    onSuccess: (data) => {
      const receipts: IReceipt[] = data.data?.data || [];
      receipts.forEach((res: any) => {
        if (res.status === 'fulfilled') {
          const refNo = res.value?.refNo || '';
          notification.success({
            message: `${refNo} ตรวจสอบสำเร็จ`,
          });
        } else {
          const refNo = res.reason?.refNo || '';
          const errorMessage = res.reason?.message || 'ตรวจสอบไม่สำเร็จ';
          notification.error({
            message: `${refNo} ${errorMessage}`,
          });
        }
      });
      const hasSuccess = receipts.some(
        (res: any) => res.status === 'fulfilled'
      );
      if (hasSuccess) {
        refetch();
        setSelectedRowKey([]);
      }
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  function onVerify(): void {
    mutationsVerify.mutateAsync(selectedRowKey);
  }

  const handleGetReceiptLines = async (record: IReceipt) => {
    try {
      const response = await getReceiptLines({
        ID: record.ID,
        TYPECODE: record.TYPECODE,
      });
      const invoiceList = response.data?.data || [];
      setInvoiceModal({
        invoiceList: invoiceList,
        open: true,
      });
    } catch (error) {
      console.error('Error fetching receipt lines:', error);
      notification.error({
        message: 'ไม่สามารถโหลดรายละเอียดใบเสร็จรับเงินได้',
      });
    }
  };

  return (
    <>
      <Flex justify="space-between" gap={1}>
        <div>
          <Title level={2} className="text-center">
            {serverInfo?.data?.data?.[0]?.SERVER_NAME}
          </Title>
        </div>
        <div>
          <Title
            level={2}
            className="text-center"
            onClick={() => (job === 'x12' ? jobProcess('billing') : undefined)}
          >
            ค้นหาใบเสร็จรับเงิน
          </Title>
        </div>
      </Flex>
      <Card>
        <Form
          {...formItemLayout}
          layout={'horizontal'}
          form={form}
          initialValues={{
            ...criteria,
            docDate: [dayjs(criteria.dateFrom), dayjs(criteria.dateTo)],
          }}
          onFinish={onFinish}
          autoComplete="off"
          labelAlign={'left'}
        >
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item name="receiptNoFrom" label="เลขที่เอกสาร (จาก)">
                <ReceiptNoSelector
                  dateFrom={dateRange.dateFrom}
                  dateTo={dateRange.dateTo}
                  handleSelectData={(receiptNo: IReceiptNo) => {
                    form.setFieldsValue({
                      receiptNoFrom: receiptNo?.ID,
                      ...(!receiptNo?.ID && { receiptNoTo: null }),
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name="receiptNoTo" label="เลขที่เอกสาร (ถึง)">
                <ReceiptNoSelector
                  disabled={!receiptNoFromValue}
                  dateFrom={dateRange.dateFrom}
                  dateTo={dateRange.dateTo}
                  handleSelectData={(receiptNo: IReceiptNo) => {
                    form.setFieldsValue({
                      receiptNoTo: receiptNo?.ID,
                    });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item<IReceiptSearchReq>
                name="customerFrom"
                label="ลูกค้า (จาก)"
              >
                <CustomerSelector
                  handleSelectData={function (customer: ICustomer): void {
                    form.setFieldsValue({
                      customerFrom: customer?.CMAD_ID,
                      ...(!customer?.CMAD_ID && { customerTo: null }),
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item<IReceiptSearchReq>
                name="customerTo"
                label="ลูกค้า (ถึง)"
              >
                <CustomerSelector
                  disabled={!customerFromValue}
                  handleSelectData={function (customer: ICustomer): void {
                    form.setFieldsValue({
                      customerTo: customer?.CMAD_ID,
                    });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item<IReceiptSearchReq> name="docStatus" label="สถานะ">
                <Select
                  allowClear
                  options={getReceiptStatusOptions()}
                  placeholder="เลือกสถานะ"
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name={'docDate'} label="วันที่เอกสาร">
                <RangePicker
                  size="large"
                  style={{ width: '100%' }}
                  locale={locale}
                  allowClear={false}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setDateRange({
                        dateFrom: dates[0].format('YYYY-MM-DD'),
                        dateTo: dates[1].format('YYYY-MM-DD'),
                      });
                      // Clear receipt number fields when date changes
                      form.setFieldsValue({
                        receiptNoFrom: null,
                        receiptNoTo: null,
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Flex gap={24} justify="center">
            <Button
              disabled={
                !selectedRowKey.length ||
                mutations.isPending ||
                selectedRowKey.some((key) => key.AR_MSTR_BMESSSTATUS === 'PC')
              }
              size="large"
              color="cyan"
              variant="solid"
              style={{ minWidth: 120 }}
              onClick={() => onEntry()}
            >
              {mutations.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'ส่งเอกสาร'
              )}
            </Button>
            <Button
              disabled={
                !selectedRowKey.length ||
                selectedRowKey.some(
                  (key) => key.AR_MSTR_BMESSSTATUS !== 'PC'
                ) ||
                mutationsVerify.isPending
              }
              size="large"
              color="orange"
              variant="solid"
              style={{ minWidth: 120 }}
              onClick={() => onVerify()}
            >
              {mutationsVerify.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'ตรวจสอบ'
              )}
            </Button>
            <Button
              size="large"
              htmlType="submit"
              className={classNames('bg-primary text-white')}
              variant="solid"
              style={{ minWidth: 120 }}
              loading={isLoading}
            >
              ค้นหา
            </Button>
            <Button
              size="large"
              color="danger"
              variant="solid"
              onClick={onClear}
              style={{ minWidth: 120 }}
            >
              ยกเลิก
            </Button>
          </Flex>
        </Form>
      </Card>
      <Card className="mt-3">
        <Table<IReceipt>
          rowSelection={{ type: 'checkbox', ...rowSelection }}
          columns={columns}
          dataSource={receipts}
          size="small"
          bordered
          rowKey="ID"
          pagination={{
            total: receipts?.length || 0,
            showTotal: (total) => `${total} รายการ`,
            showSizeChanger: true,
            locale: { items_per_page: '/หน้า' },
          }}
          locale={{
            emptyText: 'ไม่พบรายการ',
          }}
        />
      </Card>
      <SellerInvoiceModal
        open={invoiceModal?.open || false}
        invoiceList={invoiceModal.invoiceList || []}
        onClose={function (): void {
          setInvoiceModal({
            invoiceList: [],
            open: false,
          });
        }}
      />
    </>
  );
};

export default BillingPage;
